// cuisineTags.tsx
import React from 'react';
import {
  Pizza,
  Drumstick,
  Soup,
  Fish as FishIcon,
  Leaf,
  CakeSlice,
  Flame,
  Utensils,
  MilkOff,
  WheatOff,
  NutOff,
  Egg,
  Salad
} from 'lucide-react';

/** Original color lookup (restored exactly as you had it) */
export const tagColor = (tag: string): string => {
  switch (tag) {
    case 'Vegetarian':
    case 'Vegan':
      return '#0f766e'; // Teal
    case 'Veggie':
      return '#10b981'; // Green
    case 'White Meat':
      return '#ec4899'; // Pink
    case 'Red Meat':
      return '#ef4444'; // Red
    case 'Fish & Seafood':
    case 'Fish &amp; Seafood':
      return '#06b6d4'; // Cyan
    case 'Gluten Free':
      return '#fb923c'; // Orange
    case 'Dairy Free':
      return '#3b82f6'; // Blue
    case 'Low Calorie':
      return '#FFD700'; // Yellow
    case 'Low-Calorie':
      return '#FFD700'; // Yellow (alternate key)
    case 'Nut Free':
      return '#a78bfa'; // Purple
    case 'Breakfast':
    case 'Spicy':
    case 'High Energy':
      return '#ef4444'; // Red
    case 'Dessert':
    case 'Weekend Special':
    case 'Weekend':
    case 'Brunch':
      return '#ec4899'; // Pink
    case 'Seafood':
    case 'Low Fat':
    case 'Low Sugar':
      return '#3b82f6'; // Blue
    case 'High Protein':
    case 'Meal Prep':
    case 'Quick Meal':
    case 'On the Go':
    case 'Quick':
      return '#facc15'; // Yellow
    case 'Family Meal':
    case 'Family':
    case 'Holiday':
    case 'Holiday Feast':
      return '#a78bfa'; // Purple
    case 'Comfort Food':
    case 'Comfort':
      return '#f97316'; // Orange
    case 'Italian':
      // return '#10b981'; // Green (commented in original)
    case 'Chinese':
    case 'Asian':
      // return '#6366f1'; // Indigo (commented in original)
    case 'Mexican':
      // return '#facc15'; // Yellow (commented in original)
    case 'European':
      // return '#a78bfa'; // Purple (commented in original)
    case 'British':
      return '#E0C9A6'; // British / Europe-ish fallback in original
    default:
      return '#e5e7eb'; // Light Gray fallback
  }
};

const baseCircle: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: '50%',
  // border: '1px solid #fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};


const textStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 800,
  color: 'white'
};

const textStyleBlack: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 800,
  color: '#434343'
};

/** Helper to build a circle with dynamic color and label */
const circle = (tag: string, label: string) => (
  <div style={{ ...baseCircle, backgroundColor: tagColor(tag) }}>
    <span style={textStyle}>{label}</span>
  </div>
);

/** Helper to build a circle with dynamic color and label */
const circleBlackText = (tag: string, label: string) => (
  <div style={{ ...baseCircle, backgroundColor: tagColor(tag) }}>
    <span style={textStyleBlack}>{label}</span>
  </div>
);

/** Cuisine text badges (uses original tags & abbreviations) */
export const cuisineText: Record<string, JSX.Element> = {
  Italian: circle('Italian', 'IT'),
  Nigerian: circle('Nigerian', 'NG'),
  Chinese: circle('Chinese', 'CH'),
  Seafood: circle('Seafood', 'F/S'),
  Veggie: circle('Veggie', 'V'),
  Vegan: circle('Vegan', 'VE'),
  Dessert: circle('Dessert', 'DS'),
  Spicy: circle('Spicy', 'SP'),
  'Low Calorie': circleBlackText('Low Calorie', 'LC'),
  'High Protein': circle('High Protein', 'HP'),
  'Quick Meal': circle('Quick Meal', 'QM'),
  'Family Meal': circle('Family Meal', 'FM'),
  'Comfort Food': circle('Comfort Food', 'CF'),
  'Meal Prep': circle('Meal Prep', 'MP'),
  'On the Go': circle('On the Go', 'OTG'),
  'Weekend Special': circle('Weekend Special', 'WS'),
  'Holiday Feast': circle('Holiday Feast', 'HF'),
  Brunch: circle('Brunch', 'BR'),
  Comfort: circle('Comfort', 'CF'),
  Quick: circle('Quick', 'QM'),
  Family: circle('Family', 'FM'),
  Vegetarian: circle('Vegetarian', 'V'),
  'Gluten Free': circle('Gluten Free', 'GF'),
  'Low Carb': circle('Low Carb', 'LC'),
  'High Fiber': circle('High Fiber', 'HF'),
  'Low Fat': circle('Low Fat', 'LF'),
  'Low Sugar': circle('Low Sugar', 'LS'),
  'High Energy': circle('High Energy', 'HE'),
  Weekend: circle('Weekend', 'WE'),
  Holiday: circle('Holiday', 'HD'),
  'Nut Free': circle('Nut Free', 'NF'),
  'Dairy Free': circle('Dairy Free', 'DF'),
  'Red Meat': circle('Red Meat', 'RM'),
  'White Meat': circle('White Meat', 'WM'),
  'Fish & Seafood': circle('Fish & Seafood', 'F/S'),
  'Fish &amp; Seafood': circle('Fish &amp; Seafood', 'F/S'),
  Breakfast: circle('Breakfast', 'BF'),
  Healthy: circle('Healthy', 'HL'),
  Default: circle('Default', '--')
};

/** Icons for tags (keeps icon set simple and safe for lucide-react) */
export const cuisineIcons: Record<string, JSX.Element> = {
  Italian: <Pizza size={14} color="white" style={{ marginRight: 4 }} />,
  Nigerian: <Drumstick size={14} color="white" style={{ marginRight: 4 }} />,
  Chinese: <Soup size={14} color="white" style={{ marginRight: 4 }} />,
  Seafood: <FishIcon size={14} color="white" style={{ marginRight: 4 }} />,
  Veggie: <Leaf size={14} color="white" style={{ marginRight: 4 }} />,
  Vegan: <Leaf size={14} color="white" style={{ marginRight: 4 }} />,
  Dessert: <CakeSlice size={14} color="white" style={{ marginRight: 4 }} />,
  Spicy: <Flame size={14} color="white" style={{ marginRight: 4 }} />,
  'Gluten Free': <WheatOff size={14} color="white" style={{ marginRight: 4 }} />,
  'Nut Free': <NutOff size={14} color="white" style={{ marginRight: 4 }} />,
  'Dairy Free': <MilkOff size={14} color="white" style={{ marginRight: 4 }} />,
  'High Fiber': <Salad size={14} color="white" style={{ marginRight: 4 }} />,
  Breakfast: <Egg size={14} color="white" style={{ marginRight: 4 }} />,
  Default: <Utensils size={14} color="white" style={{ marginRight: 4 }} />
};

export const cuisineFlags: Record<string, string> = {
  Asian: '🇨🇳🇯🇵',
  African: '🇳🇬🇪🇹',
  European: '🇪🇺',
  'North American': '🌎',
  'South American': '🌎',
  'Middle Eastern': '🇱🇧🇸🇦',
  Caribbean: '🏝️',
  Mediterranean: '🌊',
  'Latin American': '🌎',
  American: '🇺🇸',
  British: '🇬🇧',
  Chinese: '🇨🇳',
  French: '🇫🇷',
  German: '🇩🇪',
  Greek: '🇬🇷',
  Indian: '🇮🇳',
  Italian: '🇮🇹',
  Japanese: '🇯🇵',
  Korean: '🇰🇷',
  Mexican: '🇲🇽',
  Moroccan: '🇲🇦',
  Nigerian: '🇳🇬',
  'South African': '🇿🇦',
  Spanish: '🇪🇸',
  Thai: '🇹🇭',
  Turkish: '🇹🇷',
  Vietnamese: '🇻🇳',
  Brazilian: '🇧🇷',
  Lebanese: '🇱🇧',
  Ethiopian: '🇪🇹',
  Russian: '🇷🇺',
  Canadian: '🇨🇦',
  Indonesian: '🇮🇩',
  Filipino: '🇵🇭',
  Peruvian: '🇵🇪',
  Argentinian: '🇦🇷',
  Cuban: '🇨🇺',
  Malaysian: '🇲🇾',
  Default: '🍽️'
};

export default tagColor;
